import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar, Users, CheckCircle, Clock, XCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColor = (s) => {
  if (s === "confirmed" || s === "completed") return "bg-green-100 text-green-700";
  if (s === "cancelled" || s === "failed") return "bg-red-100 text-red-700";
  return "bg-orange-100 text-orange-700";
};

const StatusIcon = ({ status }) => {
  if (status === "confirmed" || status === "completed") return <CheckCircle size={16} />;
  if (status === "cancelled" || status === "failed") return <XCircle size={16} />;
  return <Clock size={16} />;
};

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/my-bookings`, { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to load your bookings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="my-bookings-page" className="grain-texture min-h-screen">
      <Navbar />

      <section className="pt-32 pb-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="accent-label text-secondary">YOUR JOURNEY</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mt-4 mb-4 text-white">My Bookings</h1>
          <p className="text-lg text-white/80">Track your upcoming and past Andaman adventures</p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner"></div></div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-muted-foreground mb-6">You have no bookings yet.</p>
              <Link to="/packages"><Button className="bg-primary hover:bg-primary/90" data-testid="explore-packages-btn">Explore Packages</Button></Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <motion.div
                  key={booking.id}
                  data-testid={`booking-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-border/50"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-xl font-serif text-primary">{booking.package_name}</h3>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusColor(booking.booking_status)}`}>
                          <StatusIcon status={booking.booking_status} />
                          {booking.booking_status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Calendar size={14} />{booking.travel_date}</span>
                        <span className="flex items-center gap-1"><Users size={14} />{booking.num_adults} Adults · {booking.num_children} Children</span>
                        <span>Ref: <code className="text-xs bg-muted px-2 py-0.5 rounded">{booking.id.slice(0, 8)}</code></span>
                      </div>
                    </div>
                    <div className="text-right md:pl-4 md:border-l md:border-border">
                      <p className="text-2xl font-bold text-primary">₹{booking.total_amount.toLocaleString('en-IN')}</p>
                      <p className={`text-xs mt-1 ${statusColor(booking.payment_status)} inline-block px-2 py-0.5 rounded`}>
                        {booking.payment_status}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MyBookings;
